/* eslint-disable react/no-array-index-key */
import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Stack,
  Card,
  CardContent
} from '@mui/material';
import { ArrowUpward, ArrowDownward, Delete } from '@mui/icons-material';
import SpinningIcon from '@components/SpinningIcon';
import { useGetPageDeal, updatePostContent } from '@hooks/useGetPageDeal';
import MarkdownRenderer from '@components/MarkdownRenderer';
import { useGetAIFix } from '@hooks/useAI';

interface PageDealContentItem {
  heading: string;
  content: string;
  order: number;
}

interface PageDealContentProps {
  pageDealSlug: string;
}

export interface PageDealContentEditorRef {
  save: () => void;
}

/** Helper to always fix order = index + 1 after any array reordering. */
function fixOrder(items: PageDealContentItem[]): PageDealContentItem[] {
  return items.map((item, i) => ({
    ...item,
    order: i + 1
  }));
}

const PageDealContentEditor = forwardRef<
  PageDealContentEditorRef,
  PageDealContentProps
>(({ pageDealSlug }, ref) => {
  const { data, isLoading } = useGetPageDeal(pageDealSlug);
  const [contents, setContents] = useState<PageDealContentItem[]>([]);
  const { mutate: savePostContent, isLoading: isSaving } = updatePostContent();
  const { mutate: getAIFix, isLoading: isFixing } = useGetAIFix();

  // Sync local state with fetched data whenever "data" changes
  useEffect(() => {
    if (data?.pageDeal?.content) {
      // Sort by `order` if needed
      const sorted = [...data.pageDeal.content].sort(
        (a, b) => a.order - b.order
      );
      // Also ensure no gaps in the DB data
      const fixed = fixOrder(sorted);
      setContents(fixed);
    }
  }, [data?.pageDeal?.content]);

  // Handlers
  const handleHeadingChange = (index: number, newHeading: string) => {
    setContents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], heading: newHeading };
      return updated;
    });
  };

  const handleContentChange = (index: number, newContent: string) => {
    setContents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], content: newContent };
      return updated;
    });
  };

  const moveUp = (index: number) => {
    if (index === 0) return;
    setContents((prev) => {
      const updated = [...prev];
      // Swap items
      [updated[index - 1], updated[index]] = [
        updated[index],
        updated[index - 1]
      ];
      // Re-fix order
      return fixOrder(updated);
    });
  };

  const moveDown = (index: number) => {
    setContents((prev) => {
      if (index === prev.length - 1) return prev;
      const updated = [...prev];
      // Swap items
      [updated[index + 1], updated[index]] = [
        updated[index],
        updated[index + 1]
      ];
      // Re-fix order
      return fixOrder(updated);
    });
  };

  const handleDelete = (index: number) => {
    setContents((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return fixOrder(updated);
    });
  };

  const handleAddSection = () => {
    setContents((prev) => {
      // Add new blank item to the end
      const newItems = [
        ...prev,
        { heading: '', content: '', order: prev.length + 1 }
      ];
      // Re-fix to ensure correct numbering
      return fixOrder(newItems);
    });
  };

  const handleSave = () => {
    if (!data?.pageDeal) return;
    savePostContent({
      pageDealId: data.pageDeal.id,
      content: contents,
      slug: pageDealSlug
    });
  };

  // Expose `handleSave` to the parent via `ref`
  useImperativeHandle(ref, () => ({
    save: handleSave
  }));

  // If there's no pageDeal, we're still loading or there's an error
  if (!data?.pageDeal) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {data?.pageDeal?.title}
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
        {data?.pageDeal?.description}
      </Typography>

      <Stack spacing={2}>
        {contents.map((item, index) => (
          <Card key={index}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Heading"
                    fullWidth
                    value={item.heading}
                    onChange={(e) => handleHeadingChange(index, e.target.value)}
                  />
                  <Stack>
                    <IconButton onClick={() => moveUp(index)}>
                      <ArrowUpward />
                    </IconButton>
                    <IconButton onClick={() => moveDown(index)}>
                      <ArrowDownward />
                    </IconButton>
                  </Stack>
                  <IconButton
                    onClick={() => handleDelete(index)}
                    color="error"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
                <Box>
                  <Button
                    disabled={isFixing || !item.heading}
                    startIcon={
                      isFixing ? <SpinningIcon isLoading={isFixing} /> : null
                    }
                    onClick={() => {
                      getAIFix(
                        {
                          text: item.heading,
                          prompt: `Fix the grammar and spelling in this section heading for a deals blog keeping in mind H2 SEO best practices. Do not prepend with "h2" just give me the text`
                        },
                        {
                          onSuccess: (data) => {
                            handleHeadingChange(index, data.suggestion);
                          }
                        }
                      );
                    }}
                  >
                    Fix heading
                  </Button>
                </Box>
                <TextField
                  label="Content"
                  multiline
                  minRows={3}
                  fullWidth
                  value={item.content}
                  onChange={(e) => handleContentChange(index, e.target.value)}
                />
                <Box>
                  <Button
                    disabled={isFixing || !item.content}
                    startIcon={
                      isFixing ? <SpinningIcon isLoading={isFixing} /> : null
                    }
                    onClick={() => {
                      getAIFix(
                        {
                          text: item.content,
                          prompt: `Fix the grammar and spelling in this content to read well SEO wise for an Amazon Deals blog. Preserve any markdown as markdown is the return format requested. Aim for a text readbility score of 74 or higher.`
                        },
                        {
                          onSuccess: (data) => {
                            handleContentChange(index, data.suggestion);
                          }
                        }
                      );
                    }}
                  >
                    Fix content
                  </Button>
                </Box>
                {item.content ? (
                  <Box margin="32px">
                    <MarkdownRenderer content={item.content} />
                  </Box>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Box mt={3} mb={2} display="flex" justifyContent="space-between">
        <Button variant="outlined" onClick={handleAddSection} sx={{ mr: 2 }}>
          Add Section
        </Button>
        <Button
          variant="outlined"
          onClick={handleSave}
          disabled={isSaving || isLoading}
          startIcon={isSaving ? <SpinningIcon isLoading={isSaving} /> : null}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
});

export default PageDealContentEditor;
